import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, LabelList } from 'recharts';
import * as styles from './bar-chart-card.module.scss';
import { Popover } from 'antd';
import { POSITION_INSIGHTS_TAB } from '../../talent-analytics.constant';
import { ROLE_NAME } from '../../../../utils/constants';
import { useSelector } from 'react-redux';

const BarChartCard = ({ data, showSensitiveOnly, onViewReport, setStackChartData }) => {
  const [openStackName, setOpenStackName] = useState(null);

  const roleActive = useSelector((state) => state.user.roleActive);

  const transformData = (chartData) => {
    if (!chartData || !Array.isArray(chartData)) return [];

    const positionTypes = chartData.length > 0 ? Object.keys(chartData[0]).filter((key) => key !== 'name') : [];

    const transformed = positionTypes.map((positionType) => {
      const row = { name: positionType };
      chartData.forEach((item) => {
        row[item.name] = item[positionType];
      });
      return row;
    });

    const filtered = transformed.filter((row) =>
      Object.entries(row)
        .filter(([key]) => key !== 'name')
        .some(([, value]) => value > 0)
    );

    return filtered;
  };

  const transformedData = transformData(data?.chart_data);

  // get sensitivity levels dynamically for legend
  const sensitivityLevels = data?.chart_data ? data.chart_data.map((item) => item.name) : [];

  // filter sensitivity levels for legend based on showSensitiveOnly prop
  const filteredSensitivityLevels = showSensitiveOnly ? sensitivityLevels.filter((level) => level !== 'No Level') : sensitivityLevels;

  const renderDot = (key) => {
    switch (key) {
      case 'High':
        return '#E74C3C';
      case 'Medium':
        return '#F39C12';
      case 'Low':
        return '#27AE60';
      case 'No Level':
        return '#3685D3';
      default:
        return '';
    }
  };

  const PopOverContent = ({ name, dataKey, value }) => {
    return (
      <div className={styles.popover_content}>
        <div className={styles.title}>{name}</div>
        <div className={styles.row}>
          <span className={styles.dot} style={{ backgroundColor: renderDot(dataKey) }} />
          <span className={styles.label}>{dataKey}:</span>
          <span className={styles.value}>{value}</span>
        </div>
      </div>
    );
  };

  const RoundedBarWithGap = (props) => {
    const { x, y, width, height, fill, payload, dataKey } = props;
    const gap = 4;
    const newHeight = height - gap;

    const value = payload?.[dataKey];
    const nameUnique = `${payload['name']} ${dataKey}`;

    return (
      <foreignObject x={x} y={y + gap} width={width} height={newHeight > 0 ? newHeight : 0}>
        <Popover
          content={<PopOverContent name={payload?.name} dataKey={dataKey} value={value} />}
          placement="top"
          trigger="hover"
          overlayClassName="custom_popover_position_status"
          open={nameUnique === openStackName}
          onOpenChange={(open) => {
            if (open) {
              setOpenStackName(nameUnique);
              setStackChartData((pre) => ({
                ...pre,
                // [POSITION_INSIGHTS_TAB[2]]: {
                [POSITION_INSIGHTS_TAB[1]]: {
                  xAxisName: dataKey,
                  stackName: payload?.name,
                },
              }));
            } else {
              setOpenStackName(null);
            }
          }}
        >
          <div
            style={{
              width: '100%',
              height: '100%',
              backgroundColor: fill,
              borderRadius: 4,
              cursor: 'pointer',
            }}
          />
        </Popover>
      </foreignObject>
    );
  };

  const CustomTick = ({ x, y, payload }) => {
    const paddingX = 4;
    const paddingY = 4;
    const fontSize = 12;
    const text = payload.value;

    const textLength = text.length;
    const textWidth = textLength * (fontSize * 0.6);
    const width = textWidth + paddingX * 2;
    const height = fontSize + paddingY * 2;

    return (
      <g transform={`translate(${x - width / 2}, ${y})`}>
        <rect width={width} height={height} fill="#F0F5FA" rx={height / 2} ry={height / 2} />
        <text x={width / 2} y={height / 2 + fontSize / 3} textAnchor="middle" fill="#394655" fontSize={fontSize} fontWeight={400}>
          {text}
        </text>
      </g>
    );
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div className={styles.titleWithLegend}>
          <h2 className={styles.title}>Distribution of Sensitive Positions</h2>
          <div className={styles.legend}>
            {filteredSensitivityLevels.map((level, i) => (
              <div className={styles.legendItem} key={i}>
                <span
                  className={styles.legendSquare}
                  style={{
                    background: renderDot(level),
                  }}
                />
                <div className={styles.legend_label}>{level}</div>
              </div>
            ))}
          </div>
        </div>
        <button className={styles.btn} onClick={onViewReport} disabled={![ROLE_NAME.HR_COE, ROLE_NAME.HR_ADMIN].includes(roleActive?.roleName)}>
          View report
        </button>
      </div>

      <ResponsiveContainer width="100%" height={600}>
        <BarChart data={transformedData} barSize={50} barCategoryGap={10}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} color="#CECECE" />
          <XAxis dataKey="name" tickLine={false} axisLine={false} fontSize={12} color="#394655" fontWeight={400} tick={<CustomTick />} />
          <YAxis tickLine={false} axisLine={false} domain={[0, 'auto']} fontSize={14} color="#4F5F6E" />

          {sensitivityLevels.map((level) => (
            <Bar
              key={level}
              dataKey={level}
              stackId="a"
              fill={renderDot(level)}
              shape={(props) => <RoundedBarWithGap {...props} />}
              isAnimationActive={false}
              minPointSize={5}
            >
              <LabelList
                dataKey={level}
                position="center"
                fill="#fff"
                fontSize={12}
                fontWeight={500}
                formatter={(value) => (value > 0 ? value : '')}
              />
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChartCard;
